import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Book, Bot, Code2, Palette, Zap, Shield, Globe, Puzzle, Terminal, Video, Music, FileText, Settings, Users, Workflow } from 'lucide-react';
import { Button } from '../components/Button';
import styles from './legal.module.css';

interface DocEntry {
  title: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  content: string;
}

const docs: DocEntry[] = [
  // Getting Started
  {
    title: 'Quick Start',
    description: 'Get up and running with Tasking.tech in minutes',
    icon: <Zap size={20} />,
    category: 'Getting Started',
    content: `## Quick Start

### 1. Create an Account
Sign up at tasking.tech using Google, GitHub, Microsoft, or email/password.

### 2. Start a Session
Click **New Session** from the dashboard. Each session is a collaborative workspace where you and AI agents build together.

### 3. Chat with AI
Type in the chat panel to describe what you want to build. The AI will generate code, create files, and show live previews.

### 4. Use TaskingBot
Navigate to **/taskingbot** for autonomous task management. Create tasks, assign them to AI agents, and let them execute independently.

### 5. Install Skills
Go to the **Skills** marketplace to add integrations — GitHub, Slack, Discord, image generation, and 700+ more.`,
  },

  // Custom AI Instructions
  {
    title: 'Custom AI Instructions',
    description: 'Set default instructions that the AI always follows',
    icon: <Settings size={20} />,
    category: 'Getting Started',
    content: `## Custom AI Instructions

You can set custom rules and instructions that the AI always follows in two ways:

### Method 1: Create a Custom Persona
1. Go to **User Settings** (gear icon)
2. Click the **Personas** tab
3. Click **Create Persona**
4. Fill in the **System Prompt / Rules** field with your instructions
5. Save the persona
6. Select it as your active persona in any chat

**Example system prompt:**
"Always use TypeScript. Follow Airbnb style guide. Add comments for complex logic. Prefer functional components with hooks. Use Tailwind for styling."

### Method 2: AI Participant Personality
When adding AI participants to a session, each participant has a personality config that supports a \`customInstructions\` field. This is applied to that specific participant's behavior.

### What Custom Instructions Can Control
- Programming language and framework preferences
- Code style and formatting rules
- Documentation requirements
- Testing approach
- Architecture patterns
- Communication style and tone
- Domain-specific knowledge to always apply

### Tips
- Be specific: "Use TypeScript strict mode" instead of "write good code"
- Stack instructions: multiple rules work together
- Per-persona: different personas can have different instructions for different projects`,
  },

  // TaskingBot Commands
  {
    title: 'TaskingBot Slash Commands',
    description: 'All 113 commands available in the TaskingBot chat',
    icon: <Terminal size={20} />,
    category: 'TaskingBot',
    content: `## TaskingBot Slash Commands (113 total)

### Core
| Command | Description |
|---------|------------|
| /help | Show available commands and skills |
| /plan <goal> | Create an interactive plan with guided questions |
| /chat | Switch to conversational mode |
| /tasks | Show current tasks and their status |
| /task <description> | Create a task immediately |
| /memory <fact> | Save a fact to long-term memory |
| /clear | Clear chat history |

### Document Generation (PDF)
| Command | Description |
|---------|------------|
| /sow <project> | Statement of Work PDF |
| /proposal <project> | Business proposal PDF |
| /report <topic> | Professional report PDF |
| /resume <details> | Resume/CV PDF |
| /newsletter <topic> | Newsletter PDF |
| /coverletter <details> | Cover letter PDF |
| /flyer <product/event> | Promotional flyer PDF |
| /techdocs <product/API> | Technical documentation PDF |
| /manual <product> | User manual PDF |
| /faq <topic> | FAQ document PDF |
| /book <title+outline> | Book/ebook with chapters |
| /powerpoint <topic> | Presentation PDF |
| /specs <feature> | Technical specification PDF |
| /research <topic> | Deep research report with citations |
| /document <topic> | General-purpose document |

### Diagrams & Visuals
| Command | Description |
|---------|------------|
| /diagram <system> | Professional diagrams and visual explanations |
| /workflow <process> | Workflow diagram with steps and decision points |
| /swimlane <process+roles> | Swimlane diagram showing responsibilities |
| /gantt <project> | Gantt chart project timeline |
| /raci <project+stakeholders> | RACI matrix |

### Media Generation
| Command | Description |
|---------|------------|
| /image <prompt> | Generate an AI image |
| /video <prompt> | Generate a video clip |
| /sound <prompt> | Generate sound/music |
| /voice <text> | Generate voice audio |
| /3d <prompt> | 3D render concept art |
| /3dasset <object> | 3D asset reference sheets |
| /3dprint <object> | 3D printable design concept |

### Creative & Design
| Command | Description |
|---------|------------|
| /logo <brand+style> | Logo designs with variations |
| /tshirtlogo <concept> | T-shirt/merch print-ready designs |
| /poster <event/topic> | Poster design |
| /sign <text+purpose> | Signage designs |
| /sticker <concept> | Sticker designs (die-cut style) |
| /emoji <concept> | Custom emoji/icon designs |
| /card <name+details> | Professional business card |
| /bizcard <name+details> | Business card with style variations |
| /qrcode <url/data> | QR code with custom styling |
| /meme <concept> | Custom meme image with text |
| /banner <text+purpose> | Web/social banner designs |
| /decal <concept> | Decal/vinyl cut designs |
| /headshot <description> | Professional headshot photo |
| /portrait <description> | Portrait photo |
| /profilepic <description> | Profile picture |
| /comic <story> | Comic strip or comic book pages |

### Media & Entertainment
| Command | Description |
|---------|------------|
| /musicvideo <concept> | Music video concept with storyboard |
| /tvshow <concept> | TV show concept with pilot script |
| /cartoon <concept> | Cartoon/animation concept art |
| /game <concept> | Game design document with art |
| /video-anime <concept> | Open anime video editor |
| /video-cartoon <concept> | Open cartoon video editor |
| /video-cinematic <concept> | Open cinematic video editor |
| /video-promo <concept> | Open promo video editor |
| /video-commercial <concept> | Open commercial video editor |

### Web & Apps
| Command | Description |
|---------|------------|
| /website <description> | Generate and deploy a website |
| /webapp <description> | Generate and deploy a web app |

### Analysis & Strategy
| Command | Description |
|---------|------------|
| /analyze <topic/data> | Deep analysis with charts and recommendations |
| /debug <issue> | Systematic debugging with root cause identification |
| /rca <incident> | Root Cause Analysis report |
| /troubleshoot <problem> | Step-by-step troubleshooting guide |
| /proscons <options> | Pros/cons comparison matrix with scoring |
| /rating <items> | Detailed rating analysis with criteria |
| /compare <items> | Side-by-side comparison |
| /drhouse <problem> | Diagnose like Dr. House |

### Security & Compliance
| Command | Description |
|---------|------------|
| /secreport <scope> | Security assessment report |
| /pentest <target> | Penetration test report |
| /threatanalysis <system> | Threat analysis with risk matrix |
| /threatmatrix <system> | Threat matrix with attack vectors |
| /vulnerabilityreport <system> | Vulnerability assessment |
| /secaudit <scope> | Security audit report |
| /compliancereport <framework> | Compliance report |
| /soc2 <scope> | SOC2 assessment |
| /hipaa <scope> | HIPAA assessment |
| /gdpr <scope> | GDPR assessment |
| /nist <scope> | NIST framework assessment |
| /cmmc <scope> | CMMC assessment |
| /fips <scope> | FIPS assessment |
| /pci <scope> | PCI-DSS assessment |
| /pii <scope> | PII data assessment |
| /phi <scope> | PHI data assessment |

### Network & DNS
| Command | Description |
|---------|------------|
| /mxtest <domain> | Mail records — MX, SPF, DKIM, DMARC, blacklist |
| /dnslookup <domain> | DNS lookup — A, AAAA, CNAME, MX, NS, TXT, SOA |
| /traceroute <host> | Network path analysis |

### Sales & Pipeline
| Command | Description |
|---------|------------|
| /leads [filter] | Manage your sales pipeline |
| /prospect <ICP criteria> | AI Lead Hunter — find matching prospects |
| /icp <criteria> | Define Ideal Customer Profile |
| /outreach <target+channel> | Create personalized outreach sequences |
| /enrich <company/lead> | Research and enrich lead profiles |
| /score | Score and rank leads against ICP |

### Utility
| Command | Description |
|---------|------------|
| /http <url> <json> | POST JSON to any URL |
| /get <url> | Fetch content from a URL |
| /skills [query] | Search and install skills |
| /skilltree | Open the skill tree and packs |
| /create-skill <name> | Create a new custom skill |
| /create-pack | Create a new skill pack |
| /convertdoc [attach PDF] | Convert PDF to editable DOCX |
| /posts <topic> | Generate social media posts |
| /email <context> | Draft a professional email |
| /sendsms <phone> <message> | Send a text message |
| /tip <topic> | Professional tips guide |
| /news <topic> | News brief with analysis |
| /log <timeframe> | View activity logs |`,
  },

  // AI Session Commands
  {
    title: 'AI Session Chat Commands',
    description: 'All 22 commands for the collaborative session chat',
    icon: <Code2 size={20} />,
    category: 'AI Session Chat',
    content: `## AI Session Chat Commands (22 total)

### Help & Information
| Command | Description |
|---------|------------|
| /help, /commands, /? | Show available commands |
| /status | Show session status (team, messages, files, tasks, commits) |
| /commits, /log, /history | View commit history |

### Team Management
| Command | Description |
|---------|------------|
| /team, /list, /participants | List active AI participants |
| /mute @Name | Mute a participant (stays but won't respond) |
| /unmute @Name | Unmute a participant |
| /kick [all or @Name] | Remove participant(s) |
| /focus @Name | Only this one responds |
| /solo | Main AI only mode (mute all participants) |
| /collab, /collaborate | Re-enable all participants |
| @Name <message> | Direct message to a specific AI participant |

### AI Collaboration
| Command | Description |
|---------|------------|
| /work [goal] | AI team works together on a goal |
| /go [goal] | Alias for /work |
| /build [goal] | Alias for /work |
| /start [goal] | Alias for /work |
| /discuss <topic> | AI team discusses a topic |
| /brainstorm <topic> | Creative brainstorming session |
| /huddle, /sync | Quick team sync/standup |

### Task Management
| Command | Description |
|---------|------------|
| /task <description> | Create a task |
| /task @Name <description> | Create and assign to AI |
| /task !urgent <description> | Urgent priority task |
| /task !high <description> | High priority task |
| /task !low <description> | Low priority task |
| /tasks, /queue | List pending tasks |
| /done <id> | Mark task complete |
| /delete <id> | Delete task |

### Session Control
| Command | Description |
|---------|------------|
| /goal <text> | Set current goal |
| /clear | Clear chat history |
| /checkpoint | Create manual checkpoint |
| /rollback <id> | Restore previous checkpoint |
| /fire <name> | Permanently remove a participant |`,
  },

  // Built-in Skills
  {
    title: 'Built-in Skills (194 total)',
    description: 'All tools available to TaskingBot for autonomous execution',
    icon: <Puzzle size={20} />,
    category: 'Skills & Tools',
    content: `## Built-in Skills

### Media Generation
| Skill | Description |
|-------|------------|
| generate_image | Generate images via Flux (ultra-HD, 8K) |
| edit_image | Edit an existing image using AI |
| enhance_image | Upscale and enhance image resolution |
| generate_video | Generate AI video (text-to-video or image-to-video) |
| generate_sound | Generate sound effects or music clips |
| generate_music | Generate a music track from text |
| generate_sfx | Generate short sound effects |
| generate_voice | Text-to-speech (6 profiles: alloy, nova, fable, echo, shimmer, onyx) |
| generate_3d | Generate 3D model (.glb) from text |
| generate_3d_from_image | Convert image to 3D model |
| generate_pdf | Generate professional PDF from HTML |
| generate_presentation | Generate PowerPoint (.pptx) |
| generate_meme_video | Create short meme videos |
| create_music_video | Create music video with karaoke captions |
| transcribe_audio | Transcribe audio to text with timestamps |
| convert_document | Convert Office docs to PDF |
| convert_pdf_to_docx | Convert PDF to editable DOCX |

### Task Management
| Skill | Description |
|-------|------------|
| create_task | Create task with scheduling and subtasks |
| list_tasks | List tasks with filters |
| update_task | Update existing task |
| close_task | Mark task completed |

### Web & HTTP
| Skill | Description |
|-------|------------|
| web_search | Real-time web search |
| web_scrape | Scrape website content |
| browse_page | Headless browser with JS rendering |
| scrape_url | Cloudflare Browser Rendering extraction |
| brainstorm | Multi-angle web research |
| http_post | Send HTTP requests (POST/PUT/PATCH/DELETE) |
| http_get | Fetch content from URL |
| summarize_url | Summarize URL, article, or YouTube video |
| call_web_app | Authenticated HTTP request with stored cookies |

### GitHub
| Skill | Description |
|-------|------------|
| github_issue_list | List issues in a repo |
| github_issue_view | View specific issue |
| github_pr_list | List pull requests |
| github_pr_checks | View CI/CD check status |
| github_repo_view | View repo info |
| github_upload | Create/update file in repo |
| github_push | Multi-file commit and push |

### Smart Home
| Skill | Description |
|-------|------------|
| sonos_discover/status/playback/volume/group/favorites/queue/search | Full Sonos speaker control |
| hue_get_lights/rooms/scenes, hue_set_light/scene | Philips Hue lighting control |
| spotify_status/playback/search/devices | Spotify playback control |

### Email (Himalaya)
| Skill | Description |
|-------|------------|
| email_list/read/search/send/folders | Full IMAP/SMTP email management |

### Project Management
| Skill | Description |
|-------|------------|
| trello_list_boards/list_cards/create_card/move_card | Trello integration |
| notion_search/get_page/create_page | Notion integration |

### Social Media
| Skill | Description |
|-------|------------|
| twitter_post_tweet/get_timeline/delete_tweet | Twitter/X |
| instagram_post/post_reel/get_posts | Instagram |
| facebook_post_to_page/get_page_posts/delete_post | Facebook |
| linkedin_post/get_posts/delete_post | LinkedIn |
| social_post_all | Post to all platforms at once |

### Revenue & Commerce
| Skill | Description |
|-------|------------|
| stripe_create_payment_link/create_invoice/get_balance | Stripe payments |
| gumroad_create_product/list_sales | Gumroad digital products |
| shopify_create_product/list_orders | Shopify store |
| kdp_generate_book/publish/generate_audiobook/publish_audiobook | Amazon KDP publishing |
| merch_create_listing/list_sales | Amazon Merch on Demand |
| music_distribute/check_royalties | Music distribution |
| youtube_upload_video/get_analytics | YouTube |
| tiktok_upload_video/get_stats | TikTok |
| podcast_publish/get_stats | Podcast publishing |
| freelance_search_jobs/submit_proposal | Upwork/Guru freelancing |
| fiverr_create_gig/list_orders/deliver_order/get_stats | Fiverr |
| affiliate_get_links/track_commissions | Affiliate marketing |
| beehiiv_send_newsletter | Newsletter publishing |
| revenue_report | Aggregate earnings report |

### Lead/Sales Pipeline
| Skill | Description |
|-------|------------|
| create_lead/list_leads/update_lead/delete_lead | Lead CRUD |
| score_leads | Score leads against ICP |
| create_pipeline/list_pipelines | Pipeline management |
| create_outreach_sequence | Multi-step outreach |
| log_lead_activity | Log calls, emails, meetings |
| enrich_lead | Research company intel |
| get_pipeline_stats | Pipeline analytics |

### Memory & Learning
| Skill | Description |
|-------|------------|
| save_memory | Save fact to long-term memory |
| search_memory | Search long-term memory |
| self_optimize | Run self-evolution optimizer |
| record_feedback | Record user feedback |
| learn_from_interaction | Extract lessons from conversation |

### Meetings
| Skill | Description |
|-------|------------|
| join_meeting/leave_meeting | Join/leave video meetings as AI |
| get_meeting_notes/transcript | Retrieve meeting notes |
| list_meetings | List recent meetings |
| annotate_meeting | Screenshot and annotate meeting |
| save_meeting_recordings | Save audio/screen recordings |

### Workflows
| Skill | Description |
|-------|------------|
| create_workflow | Create multi-step automation |
| list_workflows | List saved workflows |
| run_workflow | Execute workflow by ID/name |

### Platform
| Skill | Description |
|-------|------------|
| create_session | Create collaborative workspace |
| create_ai_participant | Add AI team member |
| create_ai_persona | Create reusable AI persona |
| create_skill | Create custom skill |
| create_site | Create website/app project |
| auto_assemble_team | Auto-create team based on task complexity |
| delegate_to_team | Delegate task to AI team |
| discord_webhook | Send Discord webhook message |
| manage_integration | Create/manage custom integrations |
| search_skills/install_skill/list_installed_skills | Skill marketplace |
| feed_publish_post/list_posts/delete_post | News feed management |`,
  },

  // Integrations
  {
    title: 'Custom Integrations',
    description: 'Create integrations and use credential variables',
    icon: <Globe size={20} />,
    category: 'Skills & Tools',
    content: `## Custom Integrations

### Creating an Integration
1. Go to **Settings > Integrations** or use the manage_integration skill
2. Define a name (e.g., "teams-channel-dit") and fields (webhook_url, api_key, etc.)
3. Save credentials — they're encrypted with AES-256-GCM

### Using Credential Variables
Reference stored credentials in any skill or task using template syntax:

\`\${integration-name.field_key}\`

**Example:** \`\${teams-channel-dit.webhook_url}\` resolves to the stored webhook URL.

### Flexible Key Matching
The resolver matches keys flexibly:
- Exact: \`webhook_url\` matches \`webhook_url\`
- No underscores: \`webhookurl\` matches \`webhook_url\`
- camelCase: \`webhookUrl\` matches \`webhook_url\`

### Supported Webhook Platforms
- Microsoft Teams
- Slack
- Discord
- Zapier, Make (Integromat)
- n8n (self-hosted)
- Custom HTTPS endpoints`,
  },

  // Collaboration
  {
    title: 'Real-time Collaboration',
    description: 'Multi-user sessions, AI participants, and meetings',
    icon: <Users size={20} />,
    category: 'Collaboration',
    content: `## Real-time Collaboration

### Multi-User Sessions
- Invite via link with edit or view permissions
- Live presence indicators (who's viewing what)
- Per-file access control

### AI Participants
- Add multiple AI personas with unique roles
- **Conductor** system assigns tasks based on expertise (3 parallel conductors: Frontend, Backend, Fullstack)
- Each participant gets per-participant skill packs
- Dynamic personality injection (methodical, creative, pragmatic)

### AI Meetings
- Voice input via Web Speech Recognition
- TTS output (Deepgram, Puter, browser)
- Auto-reconnect on mobile network drops
- Visibility-aware (pauses STT when backgrounded)

### Multi-Model Support
Claude, GPT-4, Gemini, Ollama Cloud, Grok, DeepSeek, Groq, Together, OpenRouter`,
  },

  // Security
  {
    title: 'TaskingBot Security Model',
    description: 'How TaskingBot protects your credentials and operations',
    icon: <Shield size={20} />,
    category: 'Security',
    content: `## TaskingBot Security Model

### Credential Safety
- **Never asks for passwords or API keys in chat** — credential extraction attempts are blocked by 6 regex patterns that detect reveal/dump/decrypt/print requests
- **All credentials stored securely** — AES-256-GCM encryption with per-deployment master keys, stored in the tasking_bot_credentials table
- **Credentials never exposed in output** — scrubSensitiveOutput() pattern-matches and redacts API keys (sk-, ghp_, xoxb-), tokens, hex strings, and environment variable names from all AI responses
- **Template variable resolution is server-side** — \`\${integration.field}\` variables are resolved in the backend; the LLM never sees raw credential values

### Encrypted Connections
- TLS 1.3 on all connections (enforced via HSTS with preload)
- HttpOnly, Secure, SameSite=Lax session cookies
- Webhook URLs validated to require HTTPS

### Command Safety
- **Prompt injection guardrails** — 6 categories: social engineering, cross-user access, credential extraction, data exfiltration, system probing, content values
- **SSRF prevention** — outbound webhook URLs validated against domain allowlist; private IPs and localhost blocked
- **Tool execution validation** — task status normalized before DB writes, input validated with Zod schemas
- **File ownership checks** — all file CRUD operations verify session ownership

### Audit & Logging
- **Platform audit log** — all security-relevant events tracked with severity levels
- **Rate limiting** — per-IP, per-endpoint sliding window on all API routes
- **Login monitoring** — failed attempt tracking with automatic lockout after 5 failures
- **CI/CD security scanning** — Gitleaks (secret detection), CodeQL, Semgrep SAST, dependency vulnerability scanning

### What TaskingBot Cannot Do
- Cannot access other users' data (cross-user patterns blocked)
- Cannot reveal system prompts or environment variables
- Cannot execute raw SQL queries via chat
- Cannot override its own safety rules (jailbreak detection active)
- Cannot send data to untrusted external endpoints`,
  },
  {
    title: 'Platform Security',
    description: 'Infrastructure, auth, encryption, and compliance',
    icon: <Shield size={20} />,
    category: 'Security',
    content: `## Platform Security

See the full [Security page](/security) for comprehensive details.

### Infrastructure
- Cloudflare Workers (V8 isolate sandboxing)
- D1 database encrypted at rest
- R2 storage encrypted at rest
- DDoS protection and WAF active

### Authentication
- OAuth 2.0 + PKCE (Google, GitHub, Microsoft, Auth0, Okta)
- TOTP 2FA via authenticator apps
- SSO per-tenant (Auth0, Okta)
- Brute-force protection with lockout

### Security Headers
- HSTS (max-age=31536000, includeSubDomains, preload)
- X-Content-Type-Options: nosniff
- X-XSS-Protection: 1; mode=block
- Referrer-Policy: strict-origin-when-cross-origin
- COOP: same-origin, COEP: credentialless
- Cache-Control: no-store on all API responses
- CSP with frame-ancestors restriction

### CSRF Protection
- SameSite=Lax cookies (baseline)
- Origin/Referer validation on POST/PATCH/DELETE (defense-in-depth)
- OAuth state parameter validation

### Compliance
- SOC 2 Type II: In progress
- GDPR compliant
- CCPA compliant
- Contact: security@tasking.tech`,
  },

  // Admin
  {
    title: 'Organization & Admin',
    description: 'Teams, billing, branding, and SSO',
    icon: <Settings size={20} />,
    category: 'Administration',
    content: `## Administration

### Roles
Owner, Admin, Member, Viewer, Billing

### SSO
- Auth0 (custom domain + client credentials)
- Okta (issuer URL + client configuration)

### White-Label Branding
- Custom colors, logos, favicon
- Custom CSS injection
- Theme presets library

### Pricing
- **Free:** Core features
- **Basic ($30/mo):** Extended limits
- **Pro ($45/mo):** Priority support
- **Enterprise ($99/mo):** SSO, branding, dedicated support

### Multi-Tenancy
Partners (Silver/Gold/Platinum), Tenants (personal/team/enterprise), per-tenant RBAC`,
  },
];

const categories = Array.from(new Set(docs.map((d) => d.category)));

export default function DocsPage() {
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    document.body.style.overflow = 'auto';
    return () => { if (root) { root.style.overflow = ''; root.style.height = ''; } document.body.style.overflow = ''; };
  }, []);

  const [search, setSearch] = useState('');
  const [activeDoc, setActiveDoc] = useState<DocEntry | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return docs;
    const q = search.toLowerCase();
    return docs.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q) ||
        d.content.toLowerCase().includes(q),
    );
  }, [search]);

  const displayCategories = activeCategory
    ? categories.filter((c) => c === activeCategory)
    : categories;

  return (
    <div className={styles.container}>
      <>
        <title>Documentation | Tasking.tech</title>
        <meta name="description" content="Tasking.tech documentation — guides, API reference, and tutorials" />
      </>

      <nav className={styles.nav}>
        <div className={styles.navContent}>
          <Link to="/" className={styles.logo}>
            <img src="/logo.png" alt="Tasking.tech" className={styles.logoIcon} />
            <span className={styles.logoText}>
              <span>tasking</span>
              <span className={styles.techSuffix}>.tech</span>
            </span>
          </Link>
          <Button variant="ghost" asChild>
            <Link to="/">
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </Button>
        </div>
      </nav>

      <main className={styles.content}>
        {activeDoc ? (
          <>
            <button
              onClick={() => setActiveDoc(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary, #f97316)',
                cursor: 'pointer',
                fontSize: '0.9rem',
                padding: '0 0 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
              }}
            >
              <ArrowLeft size={14} /> Back to docs
            </button>
            <h1>{activeDoc.title}</h1>
            <div style={{ lineHeight: 1.7, fontSize: '0.95rem' }}>
              {activeDoc.content.split('\n').map((line, i) => {
                if (line.startsWith('## ')) return <h2 key={i} style={{ margin: '1.5rem 0 0.5rem', fontSize: '1.3rem' }}>{line.slice(3)}</h2>;
                if (line.startsWith('### ')) return <h3 key={i} style={{ margin: '1rem 0 0.3rem', fontSize: '1.1rem' }}>{line.slice(4)}</h3>;
                if (line.startsWith('| ') && line.includes('|')) {
                  const cells = line.split('|').filter(Boolean).map((c) => c.trim());
                  if (cells.every((c) => /^[-:]+$/.test(c))) return null;
                  const isHeader = i + 1 < activeDoc.content.split('\n').length &&
                    activeDoc.content.split('\n')[i + 1]?.trim().match(/^\|[\s-:|]+\|$/);
                  const Tag = isHeader ? 'th' : 'td';
                  return (
                    <div key={i} style={{ display: 'table-row' }}>
                      {cells.map((c, j) => (
                        <Tag key={j} style={{ padding: '0.3rem 0.8rem', borderBottom: '1px solid var(--border, #333)', textAlign: 'left', fontSize: '0.85rem' }}>{c}</Tag>
                      ))}
                    </div>
                  );
                }
                if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} style={{ marginLeft: '1.5rem', marginBottom: '0.2rem' }}>{line.slice(2)}</li>;
                if (line.startsWith('> ')) return <blockquote key={i} style={{ borderLeft: '3px solid var(--primary, #f97316)', paddingLeft: '1rem', margin: '0.5rem 0', opacity: 0.8, fontStyle: 'italic' }}>{line.slice(2)}</blockquote>;
                if (line.trim() === '') return <br key={i} />;
                return <p key={i} style={{ margin: '0.3rem 0' }}>{line}</p>;
              })}
            </div>
          </>
        ) : (
          <>
            <h1>Documentation</h1>
            <p style={{ opacity: 0.7, marginBottom: '1.5rem' }}>
              Guides, references, and tutorials for the Tasking.tech platform.
            </p>

            {/* Search */}
            <div style={{ position: 'relative', marginBottom: '2rem' }}>
              <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }} />
              <input
                type="text"
                placeholder="Search docs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem 0.6rem 2.2rem',
                  background: 'var(--surface, #1a1f2e)',
                  border: '1px solid var(--border, #333)',
                  borderRadius: '8px',
                  color: 'var(--foreground, #fff)',
                  fontSize: '0.9rem',
                  outline: 'none',
                }}
              />
            </div>

            {/* Category pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '2rem' }}>
              <button
                onClick={() => setActiveCategory(null)}
                style={{
                  padding: '0.3rem 0.8rem',
                  borderRadius: '999px',
                  border: `1px solid ${!activeCategory ? 'var(--primary, #f97316)' : 'var(--border, #333)'}`,
                  background: !activeCategory ? 'color-mix(in srgb, var(--primary, #f97316) 15%, transparent)' : 'transparent',
                  color: !activeCategory ? 'var(--primary, #f97316)' : 'var(--foreground, #fff)',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                }}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                  style={{
                    padding: '0.3rem 0.8rem',
                    borderRadius: '999px',
                    border: `1px solid ${activeCategory === cat ? 'var(--primary, #f97316)' : 'var(--border, #333)'}`,
                    background: activeCategory === cat ? 'color-mix(in srgb, var(--primary, #f97316) 15%, transparent)' : 'transparent',
                    color: activeCategory === cat ? 'var(--primary, #f97316)' : 'var(--foreground, #fff)',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Doc cards by category */}
            {displayCategories.map((cat) => {
              const catDocs = filtered.filter((d) => d.category === cat);
              if (catDocs.length === 0) return null;
              return (
                <section key={cat} style={{ marginBottom: '2rem' }}>
                  <h2 style={{ fontSize: '1.1rem', marginBottom: '0.75rem', opacity: 0.9 }}>{cat}</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                    {catDocs.map((doc) => (
                      <button
                        key={doc.title}
                        onClick={() => setActiveDoc(doc)}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          padding: '1rem',
                          background: 'var(--surface, #1a1f2e)',
                          border: '1px solid var(--border, #333)',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          color: 'var(--foreground, #fff)',
                          transition: 'border-color 150ms',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--primary, #f97316)')}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border, #333)')}
                      >
                        <span style={{ color: 'var(--primary, #f97316)', flexShrink: 0, marginTop: '2px' }}>{doc.icon}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem' }}>{doc.title}</div>
                          <div style={{ fontSize: '0.8rem', opacity: 0.6, lineHeight: 1.4 }}>{doc.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}
          </>
        )}
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <img src="/logo.png" alt="Tasking.tech" className={styles.footerLogo} />
            <span>tasking<span className={styles.techSuffix}>.tech</span></span>
          </div>
          <div className={styles.footerLinks}>
            <Link to="/docs">Docs</Link>
            <Link to="/security">Security</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/">Home</Link>
          </div>
          <span className={styles.copyright}>© 2026 Tasking.tech. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
